import React, { useState, useCallback, useEffect, useRef } from 'react';

export interface EditableCellProps {
  /** The current value of the cell */
  value: string;
  /** Row index for the cell (used in onUpdate callback) */
  rowIndex: number;
  /** Column identifier (used in onUpdate callback) */
  column: string;
  /** Callback when value is updated */
  onUpdate: (rowIndex: number, column: string, value: string) => void;
  /** Whether the cell is currently being updated */
  isUpdating?: boolean;
  /** Debounce delay in milliseconds (default: 1000) */
  debounceMs?: number;
  /** Placeholder text when value is empty */
  placeholder?: string;
  /** Whether the cell is read-only */
  readOnly?: boolean;
  /** Custom class name for the input */
  inputClassName?: string;
  /** Custom class name for the display container */
  displayClassName?: string;
  /** Type of input (default: text) */
  type?: 'text' | 'number' | 'email' | 'tel' | 'url';
}

/**
 * EditableCell - A reusable inline editable cell component
 *
 * Provides click-to-edit functionality with:
 * - Debounced updates to prevent excessive API calls
 * - Visual feedback for updating state
 * - Keyboard support (Enter to save, Escape to cancel)
 * - Auto-focus and select on edit
 *
 * @example
 * ```tsx
 * <EditableCell
 *   value={vendor.name}
 *   rowIndex={vendor.row_index}
 *   column="A"
 *   onUpdate={handleCellUpdate}
 *   isUpdating={updatingCells.has(`${vendor.row_index}-A`)}
 * />
 * ```
 */
const EditableCell: React.FC<EditableCellProps> = ({
  value,
  rowIndex,
  column,
  onUpdate,
  isUpdating = false,
  debounceMs = 1000,
  placeholder = 'Click to edit',
  readOnly = false,
  inputClassName,
  displayClassName,
  type = 'text',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Focus and select on edit start
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the update
      debounceRef.current = setTimeout(() => {
        if (newValue !== value) {
          onUpdate(rowIndex, column, newValue);
        }
      }, debounceMs);
    },
    [value, rowIndex, column, onUpdate, debounceMs]
  );

  const handleBlur = useCallback(() => {
    setIsEditing(false);

    // Clear pending debounce and trigger immediate update if changed
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (localValue !== value) {
      onUpdate(rowIndex, column, localValue);
    }
  }, [localValue, value, rowIndex, column, onUpdate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleBlur();
      }
      if (e.key === 'Escape') {
        setLocalValue(value);
        setIsEditing(false);
      }
    },
    [handleBlur, value]
  );

  const handleClick = useCallback(() => {
    if (!readOnly) {
      setIsEditing(true);
    }
  }, [readOnly]);

  // Editing mode - show input
  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={
          inputClassName ||
          'w-full px-2 py-1 border border-indigo-500 dark:border-indigo-400 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
        }
      />
    );
  }

  // Display mode - show value with click-to-edit
  return (
    <div
      onClick={handleClick}
      className={
        displayClassName ||
        `px-2 py-1 ${readOnly ? '' : 'cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/30'} rounded min-h-[28px] text-sm truncate text-gray-900 dark:text-white ${
          isUpdating ? 'bg-yellow-50 dark:bg-yellow-900/30 animate-pulse' : ''
        }`
      }
      title={localValue || placeholder}
    >
      {localValue || <span className="text-gray-400 dark:text-gray-500 italic">-</span>}
    </div>
  );
};

export default EditableCell;
